<template>
  <a
    class="health-forecast-teaser"
    :class="{ [`health-forecast-teaser--width-${teaser.width}`]: true }"
    :href="teaser.url"
    :target="teaser.target"
  >
    <div class="health-forecast-teaser__container">
      <div v-if="teaser.image" class="health-forecast-teaser__picture">
        <div class="health-forecast-teaser__image-container">
          <picture>
            <source
              v-for="(source, i) in imageSources"
              :key="i"
              :media="source.media"
              :srcset="source.srcset"
              :sizes="source.sizes"
            />
            <img
              :src="teaser.image"
              data-san-object-fit="cover"
              data-san-object-position="left"
              class="health-forecast-teaser__image"
            />
          </picture>
        </div>
      </div>
      <div class="health-forecast-teaser__wrapper">
        <div class="health-forecast-teaser__content">
          <h4 class="health-forecast-teaser__title">{{ teaser.title }}</h4>
          <p v-if="teaser.width > 1" class="health-forecast-teaser__text">
            {{ teaser.text }}
          </p>
        </div>
        <div class="health-forecast-teaser__bottom">
          <div class="health-forecast-teaser__bottom-button">
            <sanitas-button primary>{{ teaser.buttonText }}</sanitas-button>
          </div>
        </div>
      </div>
    </div>
  </a>
</template>

<script>
export default {
  name: 'HealthForecastTeaser',

  components: {
    SanitasButton: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/SanitasButton'
      )
    },
  },

  props: {
    teaser: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    imageSources() {
      if (this.teaser.imageSources && this.teaser.width) {
        let mapper = {
          1: { key: 'tall', sizes: '(min-width: 1200px) 350px, 33vw' },
          2: { key: 'tall', sizes: '(min-width: 1200px) 350px, 33vw' },
          3: { key: 'tall', sizes: '(min-width: 1200px) 350px, 33vw' },
          4: { key: 'regular', sizes: '(min-width: 1200px) 750px, 66vw' },
        }
        let sources = []
        if (mapper[this.teaser.width]) {
          sources.push({
            media: '(min-width: 860px)',
            srcset: this.teaser.imageSources[mapper[this.teaser.width].key],
            sizes: this.teaser.imageSources[mapper[this.teaser.width].sizes],
          })
        }
        sources.push({
          media: '(max-width: 860px) and (min-width: 490px)',
          srcset: this.teaser.imageSources.regular,
          sizes: '50vw',
        })
        sources.push({
          media: '(max-width: 490px)',
          srcset: this.teaser.imageSources.wide,
          sizes: '100vw',
        })
        return sources
      }
      return undefined
    },
  },
}
</script>

<style lang="postcss" scoped>
.health-forecast-teaser {
  @mixin teaserHoverZoom .health-forecast-teaser__container
    .health-forecast-teaser__image;

  width: 100%;
  padding: 6px 0;
  text-decoration: none;

  &:hover {
    text-decoration: none;
    border-bottom: none;
  }
}

.health-forecast-teaser__wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
}

.health-forecast-teaser__container {
  transition: 0.3s;
  width: 100%;
  height: auto;
  min-height: calc((100vw - 490px) * 0.3125 + 365px);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: var(--c-primary-neutral-2);
}

.health-forecast-teaser__image-container {
  width: 100%;
  flex-grow: 0;
  position: relative;
  overflow: hidden;
  padding-top: 74.42529%;
}

.health-forecast-teaser__image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  object-fit: cover;
}

.health-forecast-teaser--width-1,
.health-forecast-teaser--width-2,
.health-forecast-teaser--width-3,
.health-forecast-teaser--width-4 {
  .health-forecast-teaser__image-container {
    padding-top: 50%;
  }
}

.health-forecast-teaser__content {
  float: left;
  flex-grow: 2;
  border-top: none;
  color: var(--c-primary-neutral-1);
  padding: 20px 20px 0 20px;
}

.health-forecast-teaser {
  /* Because of magazine teaser stlyes these need to be more specific */
  .health-forecast-teaser__title {
    font-weight: bold;
    font-size: 30px;
    line-height: 30px;
    color: var(--c-primary-neutral-1);
    font-family: 'Faustina', serif;
  }

  .health-forecast-teaser__text {
    display: none;
    color: var(--c-primary-neutral-1);
    padding-top: 10px;
    font-size: 15px;
    line-height: 19px;
    overflow-wrap: break-word;
  }
}

.health-forecast-teaser__bottom {
  float: right;
  flex-grow: 0;
  margin: 20px 20px 20px 20px;
  display: flex;
}

.health-forecast-teaser__bottom-button {
  width: 100%;

  button {
    width: 100%;
  }
}

@media (--xs) {
  .health-forecast-teaser {
    width: calc((100% - 20px) / 2);
    padding: 0;
  }

  .health-forecast-teaser__container {
    min-height: calc((100vw - 860px) * 0.5 + 440px);
  }

  .health-forecast-teaser--width-1,
  .health-forecast-teaser--width-2,
  .health-forecast-teaser--width-3,
  .health-forecast-teaser--width-4 {
    .health-forecast-teaser__image-container {
      padding-top: 70.27%;
    }
  }
}

@media (min-width: 860px) {
  .health-forecast-teaser {
    &--width-1 {
      width: calc((100% - 50px) / 3);

      .health-forecast-teaser__container {
        flex-direction: column;
      }
    }

    &--width-2 {
      width: calc((100% - 25px) / 2);
    }

    &--width-3 {
      width: calc((100% - 12.5px) / 3 * 2);
    }

    &--width-4 {
      width: 100%;
    }
  }

  .health-forecast-teaser__container {
    height: 100%;
  }

  .health-forecast-teaser__container {
    min-height: calc((100vw - 1200px) * 0.25 + 440px);
  }

  .health-forecast-teaser {
    .health-forecast-teaser__text {
      display: block;
    }
  }

  .health-forecast-teaser__container {
    flex-direction: row;
  }

  .health-forecast-teaser--width-1 .health-forecast-teaser__image-container {
    padding-top: 74.42529%;
  }

  .health-forecast-teaser--width-2 .health-forecast-teaser__image-container {
    padding-top: 48.366%;
  }

  .health-forecast-teaser--width-3 .health-forecast-teaser__image-container {
    padding-top: 35.82296%;
  }

  .health-forecast-teaser--width-4 .health-forecast-teaser__image-container {
    padding-top: 29.09%;
  }

  .health-forecast-teaser--width-2,
  .health-forecast-teaser--width-3 {
    .health-forecast-teaser__picture {
      width: 50%;
    }
  }

  .health-forecast-teaser--width-4 .health-forecast-teaser__picture {
    width: 70%;
  }

  .health-forecast-teaser--width-2,
  .health-forecast-teaser--width-3,
  .health-forecast-teaser--width-4 {
    .health-forecast-teaser__wrapper {
      padding: 70px 20px 20px 20px;
    }

    .health-forecast-teaser__image-container {
      height: 100%;
    }

    .health-forecast-teaser__image {
      object-position: left;
    }
  }
}

@media (--xl) {
  .health-forecast-teaser__container {
    min-height: 440px;
  }
}
</style>
